import { darkTheme } from '../themes/dark-theme';
import { lightTheme } from '../themes/light-theme';
import { tailwindClasses } from '../themes/tailwind-classes';
import type { Theme, ThemeColors } from '../types/theme.types';

// Re-export types for backward compatibility
export type { Theme, ThemeColors } from '../types/theme.types';

// Form theme values for react-select and other JS-based styling
export const formTheme = {
	values: {
		light: {
			background: '#FFFFFF',
			backgroundDisabled: '#F9FAFB', // gray-50
			backgroundHover: '#F9FAFB', // gray-50
			backgroundSelected: '#EFF6FF', // blue-50
			border: '#D1D5DB', // gray-300
			borderHover: '#9CA3AF', // gray-400 (mirrors radioBorderHover)
			borderDisabled: '#E5E7EB', // gray-200
			borderFocus: '#3B82F6', // blue-500
			borderError: '#EF4444', // red-500
			borderWarning: '#FACC15', // yellow-400
			text: '#111827', // gray-900
			textDisabled: '#6B7280', // gray-500
			textPlaceholder: '#9CA3AF', // gray-400
			menuBackground: '#FFFFFF',
			menuBorder: '#E5E7EB', // gray-200
			optionHover: '#F9FAFB', // gray-50
			optionSelected: '#EFF6FF', // blue-50
			optionSelectedText: '#2563EB', // blue-600
			tagBackground: '#F3F4F6', // gray-100
			tagBackgroundFixed: '#E5E7EB', // gray-200
			tagText: '#374151', // gray-700
			tagTextFixed: '#4B5563', // gray-600
			// Radio button specific colors
			radioBorder: '#D1D5DB', // gray-300
			radioBorderHover: '#9CA3AF', // gray-400
			radioBorderChecked: '#3B82F6', // blue-500
			radioBorderDisabled: '#E5E7EB', // gray-200
			radioBackground: '#FFFFFF',
			radioBackgroundHover: '#F9FAFB', // gray-50
			radioBackgroundChecked: '#FFFFFF',
			radioBackgroundDisabled: '#F9FAFB', // gray-50
			radioIndicator: '#3B82F6', // blue-500
			radioIndicatorDisabled: '#9CA3AF', // gray-400
		},
		dark: {
			background: '#1F2937', // gray-800
			backgroundDisabled: '#111827', // gray-900
			backgroundHover: '#374151', // gray-700
			backgroundSelected: 'rgba(30, 58, 138, 0.3)', // blue-900/30
			border: '#4B5563', // gray-600
			borderHover: '#D1D5DB', // gray-300 (mirrors radioBorderHover)
			borderDisabled: '#374151', // gray-700
			borderFocus: '#60A5FA', // blue-400
			borderError: '#F87171', // red-400
			borderWarning: '#EAB308', // yellow-500
			text: '#F9FAFB', // gray-100
			textDisabled: '#9CA3AF', // gray-400
			textPlaceholder: '#9CA3AF', // gray-400
			menuBackground: '#1F2937', // gray-800
			menuBorder: '#374151', // gray-700
			optionHover: '#374151', // gray-700
			optionSelected: 'rgba(30, 58, 138, 0.3)', // blue-900/30
			optionSelectedText: '#60A5FA', // blue-400
			tagBackground: '#374151', // gray-700
			tagBackgroundFixed: '#4B5563', // gray-600
			tagText: '#D1D5DB', // gray-300
			tagTextFixed: '#9CA3AF', // gray-400
			// Radio button specific colors
			radioBorder: '#9CA3AF', // gray-400 - Better contrast on gray-700 background
			radioBorderHover: '#D1D5DB', // gray-300 - Even better on hover
			radioBorderChecked: '#60A5FA', // blue-400
			radioBorderDisabled: '#6B7280', // gray-500
			radioBackground: '#1F2937', // gray-800
			radioBackgroundHover: '#374151', // gray-700
			radioBackgroundChecked: '#1F2937', // gray-800
			radioBackgroundDisabled: '#111827', // gray-900
			radioIndicator: '#60A5FA', // blue-400
			radioIndicatorDisabled: '#6B7280', // gray-500
		},
	},
};

// Helper function to enrich theme with Tailwind classes
function enrichThemeWithClasses(theme: Theme): Theme {
	return {
		...theme,
		colors: {
			...theme.colors,
			classes: tailwindClasses,
		},
	};
}

// Theme name type
export const themes = {
	light: enrichThemeWithClasses(lightTheme),
	dark: enrichThemeWithClasses(darkTheme),
} as const;

export type ThemeName = keyof typeof themes;

// Whitelist of valid theme keys. Used to reject anything that isn't an
// own-property string key on `themes` — defends against `themeName === '__proto__'`
// (which would otherwise return Object.prototype from `themes[themeName]`),
// `'constructor'`, plain objects with a `toString` that returns a real theme
// name, and any non-string input.
const VALID_THEME_NAMES: ReadonlySet<ThemeName> = new Set<ThemeName>([
	'light',
	'dark',
]);

// Theme service singleton
export class ThemeService {
	private static instance: ThemeService;
	private currentTheme: Theme;
	private listeners: Array<(theme: Theme) => void> = [];

	constructor() {
		this.currentTheme = themes.light;
	}

	static getInstance(): ThemeService {
		if (!ThemeService.instance) {
			ThemeService.instance = new ThemeService();
		}
		return ThemeService.instance;
	}

	getCurrentTheme(): Theme {
		return this.currentTheme;
	}

	setTheme(themeName: ThemeName): void {
		// Validate against the explicit whitelist before indexing into `themes`.
		// `themes['__proto__']` would otherwise return Object.prototype and
		// corrupt `currentTheme`.
		if (
			typeof themeName !== 'string' ||
			!VALID_THEME_NAMES.has(themeName as ThemeName)
		) {
			return;
		}
		const theme = themes[themeName];
		if (theme !== this.currentTheme) {
			this.currentTheme = theme;
			this.notifyListeners();
		}
	}

	toggleTheme(): void {
		const newTheme = this.currentTheme.name === 'light' ? 'dark' : 'light';
		this.setTheme(newTheme);
	}

	subscribe(listener: (theme: Theme) => void): () => void {
		// Reject non-function listeners up front so the failure surfaces at the
		// subscribe call site, not later inside notifyListeners where it would
		// look like a runtime crash from an unrelated theme change.
		if (typeof listener !== 'function') {
			throw new TypeError(
				'ThemeService.subscribe requires a function listener',
			);
		}
		this.listeners.push(listener);
		return () => {
			this.listeners = this.listeners.filter((l) => l !== listener);
		};
	}

	private notifyListeners(): void {
		// Iterate over a snapshot so a listener that subscribes/unsubscribes
		// during notification doesn't shift the iteration. Each listener call
		// is isolated: a throw in one must not prevent the rest from running
		// (a single buggy subscriber should not break theme propagation across
		// the whole app).
		const snapshot = this.listeners.slice();
		snapshot.forEach((listener) => {
			try {
				listener(this.currentTheme);
			} catch (err) {
				console.error('ThemeService: listener threw during notify', err);
			}
		});
	}

	// Helper method to get colors
	getColors(): ThemeColors {
		// Return a fresh shallow-copy of the inner objects on every call so a
		// caller mutating `colors.values.primary = ...` only affects the copy
		// they hold — the next getColors() call yields a clean, original-valued
		// object. Freezing was an alternative but throws in strict mode and
		// breaks consumers that probe-then-discard.
		return {
			...this.currentTheme.colors,
			values: { ...this.currentTheme.colors.values },
			classes: { ...tailwindClasses },
		};
	}

	// Helper method to check if current theme is dark
	isDark(): boolean {
		return this.currentTheme.name === 'dark';
	}

	// Static methods for backward compatibility
	static lightTheme = lightTheme;
	static darkTheme = darkTheme;

	static getTheme(isDark: boolean): Theme {
		return isDark ? darkTheme : lightTheme;
	}

	static getColors(isDark: boolean): ThemeColors {
		return this.getTheme(isDark).colors;
	}
}

// Export singleton instance
export const themeService = ThemeService.getInstance();

/**
 * Look up a value in a status map without falling through to `Object.prototype`
 * properties. `someMap[status]` returns `Object.prototype` for `'__proto__'`
 * and the `Object` constructor for `'constructor'` — both truthy, both
 * silently corrupt the resulting className. This helper short-circuits any
 * key that isn't a true own-property string.
 */
function _safeStatusLookup(
	statusMap: Record<string, string>,
	status: unknown,
): string | undefined {
	let key: string;
	if (typeof status === 'string') {
		key = status;
	} else if (typeof status === 'number' && Number.isFinite(status)) {
		// Numeric status codes (e.g. order status `1`) are commonly looked up
		// against string keys (`'1'`). Coerce here rather than burdening every
		// callsite, but only for safe finite numbers.
		key = String(status);
	} else {
		return undefined;
	}
	if (key === '') {
		return undefined;
	}
	return Object.prototype.hasOwnProperty.call(statusMap, key)
		? statusMap[key]
		: undefined;
}

// Status color helper functions
export function getOrderStatusClasses(status?: string): string {
	const statusMap: Record<string, string> = {
		'1': tailwindClasses.statusOrderOpen, // Unsubmitted - blue
		'2': tailwindClasses.statusOrderProcessing, // Pending - amber
		'3': tailwindClasses.statusOrderShipped, // Sent to ERP - sky
		'4': tailwindClasses.statusOrderDelivered, // Approved - green
		'Partially Shipped': tailwindClasses.statusOrderProcessing, // Partially Shipped - amber
		'5': tailwindClasses.statusOrderDelivered, // Shipped - green (success)
		'6': tailwindClasses.statusOrderProcessing, // Held - amber
		'7': tailwindClasses.statusOrderCancelled, // Cancelled - red
		'8': tailwindClasses.statusOrderProcessing, // Approval Required - amber
		'9': tailwindClasses.statusOrderCancelled, // Override Rejected - red
		'10': tailwindClasses.statusOrderProcessing, // Cancellation Requested - amber
		'11': tailwindClasses.statusOrderDelivered, // Override Approved - green
		'12': tailwindClasses.statusOrderOpen, // Submitted - blue
		'13': tailwindClasses.statusOrderShipped, // Quote - sky
	};
	return (
		_safeStatusLookup(statusMap, status) ?? tailwindClasses.statusOrderDefault
	);
}

export function getInvoiceStatusClasses(status?: string): string {
	const statusMap: Record<string, string> = {
		'1': tailwindClasses.statusInvoiceOpen,
		'2': tailwindClasses.statusInvoicePartial,
		'3': tailwindClasses.statusInvoicePaid,
		'4': tailwindClasses.statusInvoiceCancelled,
		OPEN: tailwindClasses.statusInvoiceOpen,
		PARTIAL: tailwindClasses.statusInvoicePartial,
		PAID: tailwindClasses.statusInvoicePaid,
		OVERDUE: tailwindClasses.statusInvoiceOverdue,
		CANCELLED: tailwindClasses.statusInvoiceCancelled,
	};
	return (
		_safeStatusLookup(statusMap, status) ?? tailwindClasses.statusInvoiceDefault
	);
}

export function getPaymentStatusClasses(status?: string): string {
	const normalizedStatus = status?.toLowerCase();
	const statusMap: Record<string, string> = {
		paid: tailwindClasses.statusPaymentPaid,
		completed: tailwindClasses.statusPaymentPaid,
		succeeded: tailwindClasses.statusPaymentPaid,
		pending: tailwindClasses.statusPaymentPending,
		processing: tailwindClasses.statusPaymentPending,
		'processing refund': tailwindClasses.statusPaymentPending,
		partial: tailwindClasses.statusPaymentPartial,
		'partially refunded': tailwindClasses.statusPaymentPartial,
		failed: tailwindClasses.statusPaymentFailed,
		declined: tailwindClasses.statusPaymentFailed,
		refunded: tailwindClasses.statusPaymentRefunded,
		// Written inline rather than as tokens, so they were invisible to the
		// token map. Same treatment as the payment default.
		voided: tailwindClasses.statusPaymentDefault,
		cancelled: tailwindClasses.statusPaymentDefault,
	};
	return (
		_safeStatusLookup(statusMap, normalizedStatus) ??
		tailwindClasses.statusPaymentDefault
	);
}

export function getCampaignStatusClasses(status?: string): string {
	const normalizedStatus = status?.toLowerCase();
	const statusMap: Record<string, string> = {
		active: tailwindClasses.statusCampaignActive,
		running: tailwindClasses.statusCampaignActive,
		scheduled: tailwindClasses.statusCampaignScheduled,
		pending: tailwindClasses.statusCampaignScheduled,
		paused: tailwindClasses.statusCampaignPaused,
		completed: tailwindClasses.statusCampaignCompleted,
		failed: tailwindClasses.statusCampaignFailed,
		error: tailwindClasses.statusCampaignFailed,
	};
	return (
		_safeStatusLookup(statusMap, normalizedStatus) ??
		tailwindClasses.statusCampaignDefault
	);
}

/**
 * Campaign-contact delivery state.
 *
 * These four used to be palette strings written inline here rather than class
 * tokens, so they were invisible to every tool that looks at the token map —
 * they are the same tinted badge as `getStatusBadgeClasses(…, 'light')` but
 * nothing connected them. Now they resolve through the same tokens, minus the
 * outline, so a change to the status palette reaches them too.
 */
export function getCampaignContactStatusClasses(status?: string): string {
	const normalizedStatus = status?.toLowerCase();
	switch (normalizedStatus) {
		case 'sent':
			return 'bg-success-tint text-success-tint-fg';
		case 'pending':
			return 'bg-warning-tint-amber-soft text-warning-tint-fg';
		case 'error':
			return 'bg-danger-tint text-danger';
		default:
			return 'bg-neutral-tint text-muted';
	}
}

export function getLotStatusClasses(status?: string): string {
	const normalizedStatus = status?.toLowerCase();
	const statusMap: Record<string, string> = {
		active: tailwindClasses.statusLotActive,
		expired: tailwindClasses.statusLotExpired,
		recalled: tailwindClasses.statusLotExpired,
		quarantine: tailwindClasses.statusLotQuarantine,
	};
	return (
		_safeStatusLookup(statusMap, normalizedStatus) ??
		tailwindClasses.statusLotDefault
	);
}

export function getStockLevelClasses(
	level: 'high' | 'medium' | 'low' | 'critical',
): string {
	const levelMap: Record<string, string> = {
		high: tailwindClasses.statusStockHigh,
		medium: tailwindClasses.statusStockMedium,
		low: tailwindClasses.statusStockLow,
		critical: tailwindClasses.statusStockCritical,
	};
	return (
		_safeStatusLookup(levelMap, level) ?? tailwindClasses.statusStockDefault
	);
}

export function getStatusBadgeClasses(
	status?: string,
	variant: 'solid' | 'light' = 'solid',
): string {
	const normalizedStatus = status?.toLowerCase();

	if (variant === 'light') {
		const statusMap: Record<string, string> = {
			active: tailwindClasses.statusBadgeSuccessLight,
			completed: tailwindClasses.statusBadgeSuccessLight,
			success: tailwindClasses.statusBadgeSuccessLight,
			delivered: tailwindClasses.statusBadgeSuccessLight,
			paid: tailwindClasses.statusBadgeSuccessLight,
			closed: tailwindClasses.statusBadgeSuccessLight,
			pending: tailwindClasses.statusBadgeWarningLight,
			processing: tailwindClasses.statusBadgeWarningLight,
			'in-progress': tailwindClasses.statusBadgeWarningLight,
			partial: tailwindClasses.statusBadgeWarningLight,
			inactive: tailwindClasses.statusBadgeInfoLight,
			draft: tailwindClasses.statusBadgeInfoLight,
			open: tailwindClasses.statusBadgeInfoLight,
			new: tailwindClasses.statusBadgeInfoLight,
			error: tailwindClasses.statusBadgeErrorLight,
			failed: tailwindClasses.statusBadgeErrorLight,
			cancelled: tailwindClasses.statusBadgeErrorLight,
			overdue: tailwindClasses.statusBadgeErrorLight,
		};
		return (
			_safeStatusLookup(statusMap, normalizedStatus) ??
			tailwindClasses.statusBadgeDefaultLight
		);
	}

	const statusMap: Record<string, string> = {
		active: tailwindClasses.statusBadgeSuccess,
		completed: tailwindClasses.statusBadgeSuccess,
		success: tailwindClasses.statusBadgeSuccess,
		delivered: tailwindClasses.statusBadgeSuccess,
		paid: tailwindClasses.statusBadgeSuccess,
		closed: tailwindClasses.statusBadgeSuccess,
		pending: tailwindClasses.statusBadgeWarning,
		processing: tailwindClasses.statusBadgeWarning,
		'in-progress': tailwindClasses.statusBadgeWarning,
		partial: tailwindClasses.statusBadgeWarning,
		inactive: tailwindClasses.statusBadgeInfo,
		draft: tailwindClasses.statusBadgeInfo,
		open: tailwindClasses.statusBadgeInfo,
		new: tailwindClasses.statusBadgeInfo,
		error: tailwindClasses.statusBadgeError,
		failed: tailwindClasses.statusBadgeError,
		cancelled: tailwindClasses.statusBadgeError,
		overdue: tailwindClasses.statusBadgeError,
	};
	return (
		_safeStatusLookup(statusMap, normalizedStatus) ??
		tailwindClasses.statusBadgeDefault
	);
}

export function getChipColor(index: number): string {
	const colorIndex = (index % 16) + 1;
	const colorKey = `chipColor${colorIndex}` as keyof typeof tailwindClasses;
	return tailwindClasses[colorKey] || tailwindClasses.chipColor1;
}

export function getChipColorByValue(theme: Theme, index: number): string {
	const colorIndex = (index % 16) + 1;
	const colorKey = `chipColor${colorIndex}` as keyof typeof theme.colors.values;
	return theme.colors.values[colorKey] || theme.colors.values.chipColor1;
}
